import React from 'react'
import { View, Text, TouchableOpacity, StyleSheet, Keyboard } from 'react-native'
import { Image } from 'expo-image'
import { useBottomSheet } from '../../customUtils/context/BottomSheetContext'
import theme from '../../../../theme'

type Condition = 'eq' | 'lt' | 'gt' | 'lte' | 'gte'

interface ModalConfig {
    title: string
    message: string
    confirm: { label: string; action: 'continue' | 'reset' }
    cancel: { label: string; action: 'continue' | 'reset' }
}

interface ValidationAlert {
    condition: Condition
    value: number
    severity: string
    modal: ModalConfig
}

export interface ValidationParentContext {
    label?: string
    media?: string
}

const evaluateCondition = (
    condition: Condition,
    inputVal: number,
    threshold: number,
): boolean => {
    switch (condition) {
        case 'eq':
            return inputVal === threshold
        case 'lt':
            return inputVal < threshold
        case 'gt':
            return inputVal > threshold
        case 'lte':
            return inputVal <= threshold
        case 'gte':
            return inputVal >= threshold
        default:
            return false
    }
}

const resolvePlaceholders = (text: string, label: string): string =>
    text.replace('{label}', label)

export const useValidationAlerts = () => {
    const { openBottomSheet, closeBottomSheet } = useBottomSheet()

    /**
     * Evalúa validation_alerts del field contra el valor ingresado.
     * Retorna una promesa que resuelve con la acción elegida por el usuario:
     *   'continue' → guardar igual
     *   'reset'    → no guardar, limpiar el input
     * Si no hay alertas o ninguna condición matchea, resuelve con 'continue' directamente.
     *
     * parentContext: si el field es hijo de una pregunta padre, pasar el label/media
     * del padre para que el modal los muestre en vez de los del field.
     */
    const checkAlerts = (
        item: any,
        inputText: string,
        parentContext?: ValidationParentContext,
    ): Promise<'continue' | 'reset'> => {
        const alerts: ValidationAlert[] = item?.validation_alerts
        if (!alerts?.length) return Promise.resolve('continue')

        const numericValue = parseFloat(inputText)
        if (isNaN(numericValue)) return Promise.resolve('continue')

        const matched = alerts.find((alert) =>
            evaluateCondition(alert.condition, numericValue, alert.value),
        )
        if (!matched) return Promise.resolve('continue')

        return new Promise((resolve) => {
            const { modal } = matched
            const label = parentContext?.label ?? item.label ?? ''
            const image = parentContext?.media
            const title = resolvePlaceholders(modal.title, label)
            const message = resolvePlaceholders(modal.message, label)

            const handleAction = (action: 'continue' | 'reset') => {
                closeBottomSheet(true)
                resolve(action)
            }

            Keyboard.dismiss()
            openBottomSheet(
                <ValidationAlertModal
                    title={title}
                    message={message}
                    image={image}
                    confirm={modal.confirm}
                    cancel={modal.cancel}
                    onAction={handleAction}
                />,
                undefined,
                false, // isModalClosable: no se cierra solo
                false, // isModalClosableTouch
                false, // isModalClosableSlice
            )
        })
    }

    return { checkAlerts }
}

interface ValidationAlertModalProps {
    title: string
    message: string
    image?: string
    confirm: { label: string; action: 'continue' | 'reset' }
    cancel: { label: string; action: 'continue' | 'reset' }
    onAction: (action: 'continue' | 'reset') => void
}

const ValidationAlertModal = ({
    title,
    message,
    image,
    confirm,
    cancel,
    onAction,
}: ValidationAlertModalProps) => (
    <View style={styles.container}>
        <View>
            {image ? (
                <Image
                    source={{ uri: image }}
                    style={styles.image}
                    contentFit='contain'
                />
            ) : (
                <Image
                    source={require('../../../../assets/outStockPlaceholder.png')}
                    style={styles.image}
                    contentFit='contain'
                />
            )}
            <Image
                source={require('../../../../assets/zeroTransparent.png')}
                style={[styles.image, { position: 'absolute' }]}
                contentFit='contain'
            />
        </View>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.message}>{message}</Text>
        <View style={styles.buttons}>
            <TouchableOpacity
                style={styles.confirmButton}
                onPress={() => onAction(confirm.action)}
            >
                <Text style={styles.confirmText}>{confirm.label}</Text>
            </TouchableOpacity>
            <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => onAction(cancel.action)}
            >
                <Text style={styles.cancelText}>{cancel.label}</Text>
            </TouchableOpacity>
        </View>
    </View>
)

const styles = StyleSheet.create({
    container: {
        paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 24,
        backgroundColor: 'white',
        alignItems: 'center',
    },
    image: {
        width: 160,
        height: 160,
        marginBottom: 0,
        borderRadius: 8,
    },
    title: {
        fontSize: 18,
        marginTop: 20,
        fontWeight: '700',
        marginBottom: 8,
        color: theme.colors.textPrimary,
        fontFamily: theme.fonts.main,
        textAlign: 'center',
    },
    message: {
        fontSize: 16,
        color: theme.colors.darkGray,
        marginBottom: 20,
        lineHeight: 22,
        fontFamily: theme.fonts.main,
        textAlign: 'center',
    },
    buttons: {
        width: '100%',
        gap: 4,
    },
    confirmButton: {
        backgroundColor: theme.colors.colorPrimary,
        borderRadius: 8,
        paddingVertical: 11,
        alignItems: 'center',
        width: '100%',
    },
    confirmText: {
        color: 'white',
        fontWeight: '700',
        fontSize: 16,
    },
    cancelButton: {
        paddingVertical: 10,
        alignItems: 'center',
        width: '100%',
    },
    cancelText: {
        color: theme.colors.colorPrimary,
        fontWeight: '700',
        fontSize: 16,
    },
})
